<template>
	<Layout>
		<PageHeader :title="title" :items="items" />
		<div class="row">
			<div class="col">
				<div class="card">
					<div class="card-body">
						<form v-if="readPermission && status.detail" @submit.prevent="requestUpdate">
							<b-form-group id="name" label-cols-sm="2" label-cols-lg="2" :label="$t('label.name')" label-for="name">
								<b-form-input
									id="name"
									v-model.trim="createForm.name"
									:class="{ 'is-invalid': submitted && $v.createForm.name.$error }"
								></b-form-input>
								<div v-if="submitted && !$v.createForm.name.required" class="invalid-feedback d-block">
									{{ $t("message.requiredX", [$t("label.name")]) }}
								</div>
							</b-form-group>
							<b-form-group id="code" label-cols-sm="2" label-cols-lg="2" :label="$t('label.code')" label-for="code">
								<b-form-input
									id="code"
									v-model.trim="createForm.code"
									:class="{ 'is-invalid': submitted && $v.createForm.code.$error }"
								></b-form-input>
								<div v-if="submitted && !$v.createForm.code.required" class="invalid-feedback d-block">
									{{ $t("message.requiredX", [$t("label.code")]) }}
								</div>
							</b-form-group>
							<b-form-group id="email" label-cols-sm="2" label-cols-lg="2" :label="$t('label.email')" label-for="email">
								<b-form-input
									id="email"
									v-model.trim="createForm.email"
									:class="{ 'is-invalid': submitted && $v.createForm.email.$error }"
								></b-form-input>
								<div v-if="submitted && !$v.createForm.email.required" class="invalid-feedback d-block">
									{{ $t("message.requiredX", [$t("label.email")]) }}
								</div>
								<div v-if="submitted && createForm.email && !$v.createForm.email.email" class="invalid-feedback d-block">
									{{ $t("message.invalidX", [$t("label.email")]) }}
								</div>
							</b-form-group>
							<b-form-group id="phone" label-cols-sm="2" label-cols-lg="2" :label="$t('label.phone')" label-for="phone">
								<vue-tel-input
									id="phone"
									v-model="createForm.phone"
									:autoDefaultCountry="true"
									:autoFormat="false"
									:class="{ 'is-invalid': submitted && $v.createForm.phone.$error }"
									@country-changed="phoneCountryChanged"
								></vue-tel-input>
								<div v-if="submitted && !$v.createForm.phone.required" class="invalid-feedback d-block">
									{{ $t("message.requiredX", [$t("label.phone")]) }}
								</div>
							</b-form-group>
							<div class="form-group row mb-0">
								<div class="col-sm-10 offset-sm-2">
									<b-button type="submit" variant="info" class="mr-2" :disabled="!status.save || !updatePermission">
										{{ $t("label.updateX", [$t("module.supplier")]) }}
									</b-button>
									<b-button type="button" variant="danger" @click="$router.go(-1)">
										{{ $t("label.cancel") }}
									</b-button>
								</div>
							</div>
						</form>
						<div v-else-if="!status.detail" class="d-flex justify-content-center align-items-center p-5 m-5">
							<b-spinner class="m-5" variant="info" role="status"></b-spinner>
						</div>
						<div v-else class="text-center">
							{{ $t("message.noPermissionMessage") }}
						</div>
					</div>
				</div>
			</div>
		</div>
	</Layout>
</template>
<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { required, email } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import { SESSION } from "@/constants";
import { sessionHelper } from "@/utils";
import { SUPPLIER_MODULE } from "@/state/modules/supplier";
import { supplierMethods } from "@/state/helpers";

export default {
	name: "SupplierUpdate",
	components: { Layout, PageHeader },
	page: {
		title: i18n.t("label.updateX", [i18n.t("module.supplier")]),
	},
	beforeRouteLeave(to, from, next) {
		if (to.name != "Supplier-listing") {
			sessionHelper.clearSearch(SESSION.SUPPLIER_SEARCH);
		}
		next();
	},
	data() {
		return {
			title: i18n.t("label.updateX", [i18n.t("module.supplier")]),
			items: [
				{
					text: i18n.t("module.supplier"),
					href: "/supplier",
				},
				{
					text: i18n.t("label.updateX", [i18n.t("module.supplier")]),
					active: true,
				},
			],
			submitted: false,
			readPermission: false,
			updatePermission: false,
			searchCriteria: {},
			createForm: this.defaultForm(),
		};
	},
	validations() {
		return {
			createForm: {
				name: { required },
				code: { required },
				email: { required, email },
				phone: { required },
			},
		};
	},
	computed: {
		...mapState(SUPPLIER_MODULE, ["status"]),
		data() {
			return this.$store.state.supplier.details;
		},
	},
	async created() {
		this.searchCriteria = sessionHelper.retrieveSearch(SESSION.SUPPLIER_SEARCH) || {};
		this.validPermissions();
		await this.getSupplierData();
	},
	methods: {
		...supplierMethods,
		defaultForm() {
			return {
				id: "",
				name: "",
				code: "",
				email: "",
				phone: "",
				phoneCode: "",
				phoneISO: "",
			};
		},
		resetForm() {
			this.createForm = this.defaultForm();
			this.submitted = false;
			this.$v.$reset();
		},
		validPermissions() {
			let permissions = this.getPermissionCodes();
			this.readPermission = permissions.indexOf("SUPPLIER_UR") > -1;
			this.updatePermission = permissions.indexOf("SUPPLIER_UU") > -1;
		},
		getPermissionCodes() {
			let permissions = sessionHelper.getPermissions();
			if (!permissions) return [];
			return JSON.parse(permissions).map((permission) => permission.code);
		},
		phoneCountryChanged(country) {
			this.createForm.phoneCode = "+" + country.dialCode;
			this.createForm.phoneISO = country.iso2;
		},
		async getSupplierData() {
			if (!this.searchCriteria.selectedRecordId) {
				this.$router.push({ name: "Supplier-listing" });
				return;
			}
			if (this.readPermission) {
				await this.getSupplier({ id: this.searchCriteria.selectedRecordId });
				this.createForm = {
					id: this.data.id,
					name: this.data.name,
					code: this.data.code,
					email: this.data.email,
					phone: this.data.phone,
					phoneCode: "",
					phoneISO: "",
				};
			}
		},
		requestUpdate() {
			this.submitted = true;
			this.$v.$touch();
			if (this.$v.$invalid) return;
			let data = { ...this.createForm };
			this.updateSupplier({ data, filter: this.searchCriteria }).then((result) =>
				this.$nextTick(() => {
					if (result?.success) {
						this.resetForm();
						this.$router.push({ name: "Supplier-listing" });
					}
				})
			);
		},
	},
};
</script>
